import { z } from "zod";
import { formatStoredPath, parseStoredPath } from "../domain/paths";
import { remapWorkspacePath } from "./workspace_move";
import type { WorkspaceGateway } from "./workspace_gateway";

export const WORKSPACE_MANIFEST_PATH = [".shotboard", "workspace.json"] as const;

export const workspaceManifestSchema = z
  .object({
    format: z.literal("shotboard-workspace"),
    schemaVersion: z.literal(1),
    createdAt: z.iso.datetime(),
    pinnedBoardPaths: z.array(z.string()).default([]),
  })
  .loose();

export type WorkspaceManifest = z.infer<typeof workspaceManifestSchema>;

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

/** Owns the portable, human-readable workspace preferences file. */
export class WorkspaceManifestStore {
  private readonly gateway: WorkspaceGateway;
  private readonly now: () => Date;

  /** Retains one workspace and a deterministic creation clock. */
  public constructor(gateway: WorkspaceGateway, now: () => Date = () => new Date()) {
    this.gateway = gateway;
    this.now = now;
  }

  /** Reads and validates portable preferences without creating hidden state. */
  public async load(): Promise<WorkspaceManifest> {
    if (!(await this.gateway.fileExists(WORKSPACE_MANIFEST_PATH))) {
      return this.createEmptyManifest();
    }
    const raw = textDecoder.decode(await this.gateway.readFile(WORKSPACE_MANIFEST_PATH));
    const manifest = workspaceManifestSchema.parse(JSON.parse(raw));
    for (const path of manifest.pinnedBoardPaths) {
      parseStoredPath(path);
    }
    return { ...manifest, pinnedBoardPaths: [...new Set(manifest.pinnedBoardPaths)].sort() };
  }

  /** Adds or removes one normalized board path through a safe manifest replacement. */
  public async togglePin(path: readonly string[]): Promise<WorkspaceManifest> {
    const storedPath = formatStoredPath(path);
    const manifest = await this.load();
    const pinned = new Set(manifest.pinnedBoardPaths);
    if (pinned.has(storedPath)) {
      pinned.delete(storedPath);
    } else {
      pinned.add(storedPath);
    }
    return this.write({ ...manifest, pinnedBoardPaths: [...pinned].sort() });
  }

  /** Follows a pinned board across a move or removes its shortcut when trashed. */
  public async remapPin(
    sourcePath: readonly string[],
    destinationPath: readonly string[] | null,
  ): Promise<WorkspaceManifest> {
    return this.remapPathPrefix(sourcePath, destinationPath);
  }

  /** Follows every pinned board nested beneath a moved folder or removes matching shortcuts. */
  public async remapPathPrefix(
    sourcePath: readonly string[],
    destinationPath: readonly string[] | null,
  ): Promise<WorkspaceManifest> {
    const manifest = await this.load();
    const remapped = manifest.pinnedBoardPaths.map((storedPath) => {
      const path = parseStoredPath(storedPath);
      if (remapWorkspacePath(path, sourcePath, sourcePath) === null) {
        return storedPath;
      }
      if (destinationPath === null) {
        return null;
      }
      const mapped = remapWorkspacePath(path, sourcePath, destinationPath);
      if (mapped === null) {
        throw new Error("A matching pinned path could not be remapped.");
      }
      return formatStoredPath(mapped);
    });
    if (remapped.every((path, index) => path === manifest.pinnedBoardPaths[index])) {
      return manifest;
    }
    return this.write({
      ...manifest,
      pinnedBoardPaths: [
        ...new Set(remapped.filter((path): path is string => path !== null)),
      ].sort(),
    });
  }

  /** Safely writes one fully validated portable workspace manifest. */
  private async write(manifest: WorkspaceManifest): Promise<WorkspaceManifest> {
    await this.ensureSystemDirectory();
    await this.gateway.writeFileSafely(
      WORKSPACE_MANIFEST_PATH,
      textEncoder.encode(`${JSON.stringify(workspaceManifestSchema.parse(manifest), null, 2)}\n`),
    );
    return manifest;
  }

  /** Creates the in-memory default used until the first portable mutation. */
  private createEmptyManifest(): WorkspaceManifest {
    return {
      format: "shotboard-workspace",
      schemaVersion: 1,
      createdAt: this.now().toISOString(),
      pinnedBoardPaths: [],
    };
  }

  /** Creates only the reserved metadata directory when first needed. */
  private async ensureSystemDirectory(): Promise<void> {
    if (!(await this.gateway.fileExists([".shotboard"]))) {
      await this.gateway.createDirectory([".shotboard"]);
    }
  }
}
